import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { fetchBankInfoData, fetchBankInfoDataError, fetchBankInfoDataSuccess, updateBankInformationData, updateBankInformationDataError, updateBankInformationDataSuccess } from "./bank-account-form.actions";
import { catchError, first, map, mergeMap, of, switchMap, tap, withLatestFrom } from "rxjs";
import { BankInfoRequestService, RoseService } from "../../services";
import { PersonalInformationIdSelector } from "../personal-information/personal-information.selectors";
import { SetBankAccountListAction } from "../bank-accounts/bank-accounts.actions";
import { EffectsBase } from "clicklease-ui-elements";

/**
 * The `BankAccountFormEffect` class defines side effects for handling bank account form-related actions.
 * It listens to dispatched actions and performs asynchronous operations, such as fetching bank information
 * or updating bank account data, and dispatches new actions based on the results.
 */
@Injectable()
export class BankAccountFormEffect extends EffectsBase {
    /**
     * Selector to retrieve the Rose ID from the store.
     * This ID is used for operations that require user-specific data.
     */
    readonly #roseId = this.store.selectSignal(PersonalInformationIdSelector);

    /**
     * Service for making requests to fetch bank information based on routing numbers.
     */
    private readonly riskManagerGatewayRequestService: BankInfoRequestService = inject(BankInfoRequestService);

    /**
     * Service for updating bank account information in the backend.
     */
    private readonly roseService: RoseService = inject(RoseService);

    /**
     * Effect to handle the `fetchBankInfoData` action.
     * 
     * - Listens for the `fetchBankInfoData` action.
     * - Calls the `getBankInfo` method of the `BankInfoRequestService` to fetch bank information based on the routing number.
     * - Dispatches `fetchBankInfoDataSuccess` with the fetched data on success.
     * - Dispatches `fetchBankInfoDataError` with error details on failure.
     */
    fetchBankInfoDataEffect = createEffect(() =>
        this.actions.pipe(
            ofType(fetchBankInfoData),
            mergeMap((data) =>
                this.riskManagerGatewayRequestService.getBankInfo(data.routingNumber).pipe(
                    first(),
                    map((item: any) => fetchBankInfoDataSuccess({ ...item })),
                    catchError(error => of(fetchBankInfoDataError({ bankAccountErrorData: { ...error.error } })))
                )
            )
        )
    );

    /**
     * Effect to handle the `updateBankInformationData` action.
     * 
     * - Listens for the `updateBankInformationData` action.
     * - Calls the `updateBankInformation` method of the `RoseService` to update bank account information.
     * - Dispatches `updateBankInformationDataSuccess` on successful update.
     * - Dispatches `updateBankInformationDataError` with error details on failure.
     */
    updateBankInformationEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(updateBankInformationData),
            mergeMap(({ bankData }) =>
                this.roseService.updateBankInformation(this.#roseId(), bankData).pipe(
                    map(() => updateBankInformationDataSuccess()),
                    catchError((error) => of(updateBankInformationDataError({ error: error.message }))),
                    first()
                )
            )
        )
    );
}